import { useEffect, useState } from "react"
import { toast } from "sonner"

import { langStore } from "@/stores/lang-store"
import {
  createAptGroupAPI,
  updateAptGroupAPI,
  deleteAptGroupAPI,
  getAptGroupsAPI
} from "@/apis/apt-groups-api"

import { editorBundle, FAVORITE_GROUP_NAME, GROUP_COLOR_MAP } from "./apt-group-const"
import { useToggleStore } from "@/hooks/use-toggle-store"
import ManageIcon from "@/components/icons/manage-icon"
import CloseIcon from "@/components/icons/close-icon"
import CheckIcon from "@/components/icons/check-icon"
import { aptGroupStore } from "./apt-group-store"

const AptGroupManager = () => {
  const { lang } = langStore()
  const text = editorBundle[lang] ?? editorBundle.ko

  const { show, close } = useToggleStore("group-manage")
  const { groups, setGroups } = aptGroupStore()

  const [editingGroup, setEditingGroup] = useState(null)
  const [groupName, setGroupName] = useState("")
  const [colorKey, setColorKey] = useState("blue")
  const [isSaving, setIsSaving] = useState(false)
  const [deletingGroupId, setDeletingGroupId] = useState(null)

  const isEditing = editingGroup !== null
  const isBusy = isSaving || deletingGroupId !== null

  const selectedColor = GROUP_COLOR_MAP[colorKey] ?? GROUP_COLOR_MAP.blue
  const visibleGroups = groups.filter((group) => group.group_name !== FAVORITE_GROUP_NAME)

  useEffect(() => {
    if (!show) {
      setEditingGroup(null)
      setGroupName("")
      setColorKey("blue")
      setIsSaving(false)
      setDeletingGroupId(null)
    }
  }, [show])

  useEffect(() => {
    if (!show) return

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isBusy) {
        close()
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [show, isBusy, close])

  if (!show) return null

  const resetForm = () => {
    setEditingGroup(null)
    setGroupName("")
    setColorKey("blue")
  }

  const handleAdd = () => {
    resetForm()
  }

  const handleEdit = (group) => {
    setEditingGroup(group)
    setGroupName(group.group_name)
    setColorKey(GROUP_COLOR_MAP[group.color_key] ? group.color_key : "blue")
  }

  const handleSave = async (event) => {
    event.preventDefault()

    if (isBusy) return

    const trimmedName = groupName.trim()

    if (!trimmedName) {
      toast.warning(text.nameRequired)
      return
    }

    if (trimmedName.length > 100) {
      toast.warning(text.nameTooLong)
      return
    }

    const normalizedName = trimmedName.toLocaleLowerCase()

    const duplicate = groups.some(
      (group) =>
        group.id !== editingGroup?.id && group.group_name.toLocaleLowerCase() === normalizedName
    )

    if (duplicate) {
      toast.warning(text.duplicate)
      return
    }

    setIsSaving(true)

    try {
      if (isEditing) {
        await updateAptGroupAPI({
          group_id: editingGroup.id,
          group_name: trimmedName,
          color_key: colorKey
        })

        toast.success(text.updateSuccess)
      } else {
        await createAptGroupAPI({
          group_name: trimmedName,
          color_key: colorKey
        })

        toast.success(text.createSuccess)
      }

      resetForm()

      const result = await getAptGroupsAPI()
      setGroups(result)
    } catch (error) {
      const status = error?.response?.status
      toast.error(status === 409 ? text.duplicate : text.saveError)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (group) => {
    if (isBusy) return
    if (!window.confirm(text.confirmDelete(group.group_name))) return

    setDeletingGroupId(group.id)

    try {
      await deleteAptGroupAPI({ group_id: group.id })

      if (editingGroup?.id === group.id) {
        resetForm()
      }

      toast.success(text.deleteSuccess)

      const result = await getAptGroupsAPI()
      setGroups(result)
    } catch {
      toast.error(text.deleteError)
    } finally {
      setDeletingGroupId(null)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isBusy) {
          close()
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="apt-group-editor-title"
        className="flex max-h-[min(88vh,760px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-5 sm:px-6">
          <div className="min-w-0">
            <div className="mb-1.5 flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <ManageIcon />
              </span>

              <h2
                id="apt-group-editor-title"
                className="text-lg font-bold tracking-tight text-slate-900"
              >
                {text.title}
              </h2>
            </div>

            <p className="text-sm leading-5 text-slate-500">{text.description}</p>
          </div>

          <button
            type="button"
            onClick={close}
            disabled={isBusy}
            aria-label={text.close}
            className="shrink-0 rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
          >
            <CloseIcon size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-5 sm:p-6">
          <section>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex items-baseline gap-2">
                <h3 className="text-sm font-bold text-slate-800">{text.groups}</h3>

                <span className="text-xs font-medium tabular-nums text-slate-400">
                  {visibleGroups.length}
                </span>
              </div>

              <button
                type="button"
                onClick={handleAdd}
                disabled={isBusy}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-blue-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-600 active:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>

                {text.add}
              </button>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white">
              {visibleGroups.length === 0 ? (
                <div className="flex flex-col items-center px-4 py-9 text-center">
                  <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      className="h-5 w-5"
                      aria-hidden="true"
                    >
                      <rect x="4" y="4" width="16" height="16" rx="3" />
                      <path d="M12 8v8M8 12h8" />
                    </svg>
                  </span>

                  <p className="text-sm font-semibold text-slate-700">{text.empty}</p>

                  <p className="mt-1 text-xs leading-5 text-slate-400">{text.emptyDescription}</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {visibleGroups.map((group) => {
                    const color = GROUP_COLOR_MAP[group.color_key] ?? GROUP_COLOR_MAP.blue

                    const isCurrentEditing = editingGroup?.id === group.id

                    return (
                      <div
                        key={group.id}
                        className={`flex min-w-0 items-center gap-3 px-3.5 py-3 transition-colors sm:px-4 ${
                          isCurrentEditing ? "bg-blue-50/60" : "hover:bg-slate-50/80"
                        }`}
                      >
                        <span
                          className={`h-3 w-3 shrink-0 rounded-full ${color.ballColor}`}
                          aria-hidden="true"
                        />

                        <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                          {group.group_name}
                        </p>

                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleEdit(group)}
                            disabled={isBusy}
                            aria-label={`${text.edit}: ${group.group_name}`}
                            title={text.edit}
                            className={`flex h-8 w-8 items-center justify-center rounded-lg transition disabled:cursor-not-allowed disabled:opacity-50 ${
                              isCurrentEditing
                                ? "bg-blue-100 text-blue-700"
                                : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                            }`}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.7"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="h-4 w-4"
                              aria-hidden="true"
                            >
                              <path d="m16 4 4 4" />
                              <path d="M4 20h4l11-11a2.1 2.1 0 0 0-4-4L4 16v4Z" />
                            </svg>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(group)}
                            disabled={isBusy}
                            aria-label={`${text.delete}: ${group.group_name}`}
                            title={text.delete}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {deletingGroupId === group.id ? (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                className="h-4 w-4 animate-spin"
                                aria-hidden="true"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="9"
                                  stroke="currentColor"
                                  strokeOpacity=".25"
                                  strokeWidth="3"
                                />
                                <path
                                  d="M21 12a9 9 0 0 0-9-9"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                />
                              </svg>
                            ) : (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-4 w-4"
                                aria-hidden="true"
                              >
                                <path d="M3 6h18" />
                                <path d="M8 6V4h8v2" />
                                <path d="m19 6-1 14H6L5 6" />
                                <path d="M10 11v5M14 11v5" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </section>

          <form
            onSubmit={handleSave}
            className="rounded-2xl border border-slate-100 bg-slate-50 p-4 sm:p-5"
          >
            <div className="mb-5 flex items-center gap-2.5">
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-lg ${selectedColor.bgColor} ${selectedColor.textClassName}`}
              >
                {isEditing ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <path d="m16 4 4 4" />
                    <path d="M4 20h4l11-11a2.1 2.1 0 0 0-4-4L4 16v4Z" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                )}
              </span>

              <div className="min-w-0">
                <h3 className="text-sm font-bold text-slate-800">
                  {isEditing ? text.editTitle : text.createTitle}
                </h3>

                <p className="mt-0.5 text-xs text-slate-500">{isEditing ? text.edit : text.add}</p>
              </div>
            </div>

            <div>
              <label
                htmlFor="apt-group-name"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                {text.name}
              </label>

              <input
                id="apt-group-name"
                type="text"
                maxLength={100}
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
                placeholder={text.namePlaceholder}
                disabled={isBusy}
                className="w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-3 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            <div className="mt-5">
              <p className="mb-3 text-sm font-semibold text-slate-700">{text.color}</p>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-4 px-1 py-1">
                {Object.entries(GROUP_COLOR_MAP).map(([key, color]) => {
                  const isSelected = colorKey === key

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setColorKey(key)}
                      disabled={isBusy}
                      aria-label={key}
                      aria-pressed={isSelected}
                      className={`flex h-8 w-8 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-50 ${
                        color.ballColor
                      } ${
                        isSelected ? `ring-2 ${color.ringColor} ring-offset-2` : "hover:scale-110"
                      }`}
                    >
                      {isSelected && <CheckIcon stroke={key === "yellow" ? "#713f12" : "white"} />}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={isBusy}
                  className="rounded-lg min-w-16 border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
                >
                  {text.cancel}
                </button>
              )}

              <button
                type="submit"
                disabled={isBusy}
                className="inline-flex min-w-16 items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving && (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                      stroke="currentColor"
                      strokeOpacity=".25"
                      strokeWidth="3"
                    />
                    <path
                      d="M21 12a9 9 0 0 0-9-9"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                )}

                {!isSaving && text.save}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  )
}

export default AptGroupManager
